/**
 * Evaluation Add Page - Run Evaluation
 * Run evaluation with datasets, deployments, and metrics selection
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ArrowBack as ArrowBackIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { evaluationService } from '../../services/EvaluationService';
import { datasetService } from '../../services/DatasetService';
import { ROUTE_PATHS } from '../../constants/routePaths';
import api from '../../interceptors/interceptor';
import { API_ROUTES } from '../../constants/apiRoutes';

interface Metric {
  metric_id: number;
  metric_name: string;
  metric_type: string;
  description: string;
}

interface MetricsResponse {
  [category: string]: Metric[];
}

interface Dataset {
  dataset_id: number;
  name: string;
  is_active: boolean;
}

interface Deployment {
  instance_id?: string;
  instance_name?: string;
  name?: string;
}

interface PromptVersion {
  id: string;
  version_tag: number;
}

interface PromptTemplate {
  id: string;
  name: string;
  versions: PromptVersion[];
}

export function EvaluationAddPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableMetrics, setAvailableMetrics] = useState<MetricsResponse>({});
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [selectedPromptVersions, setSelectedPromptVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [versionSearchTerm, setVersionSearchTerm] = useState('');

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name must be less than 100 characters'),
    dataset_id: Yup.string().required('Dataset is required'),
    deployment_id: Yup.array()
      .min(1, 'At least one deployment must be selected')
      .required('Deployment is required'),
    judge_model: Yup.string().required('Judge model is required'),
    prompt_template_id: Yup.string().required('Prompt template is required'),
    prompt_template_version: Yup.array()
      .min(1, 'At least one version must be selected')
      .required('Prompt template version is required'),
    selectedMetrics: Yup.array()
      .min(1, 'At least one metric must be selected')
      .required('Metrics selection is required'),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      name: '',
      dataset_id: '',
      deployment_id: [] as string[],
      judge_model: '',
      prompt_template_id: '',
      prompt_template_version: [] as string[],
      selectedMetrics: [] as string[],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Build metrics object grouped by category
        const metrics: any = {};
        values.selectedMetrics.forEach(metricName => {
          // Find which category this metric belongs to
          for (const [category, metricsList] of Object.entries(availableMetrics)) {
            const metric = metricsList.find((m: Metric) => m.metric_name === metricName);
            if (metric) {
              if (!metrics[category]) {
                metrics[category] = [];
              }
              metrics[category].push({ name: metric.metric_name });
              break;
            }
          }
        });

        // Transform to match API format
        const payload = {
          name: values.name,
          dataset_id: parseInt(values.dataset_id),
          deployment_id: values.deployment_id,
          judge_model: values.judge_model,
          prompt_template_id: values.prompt_template_id,
          prompt_template_version: values.prompt_template_version,
          metrics: metrics,
        };

        toast.info('Running evaluation...');
        
        // Call the API
        await evaluationService.createEvaluation(payload);

        toast.success('Evaluation run started successfully');
        formik.resetForm();
        
        // Navigate back to evaluations list page
        setTimeout(() => {
          navigate(ROUTE_PATHS.EVALUATION);
        }, 1500);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to run evaluation';
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    try {
      fetchInitialData();
    } catch (err) {
      console.error('❌ Error calling fetchInitialData:', err);
    }
  }, []);


  useEffect(() => {    
    if (formik.values.prompt_template_id) {
      const template = promptTemplates.find(t => t.id === formik.values.prompt_template_id);      
      setSelectedPromptVersions(template?.versions || []);
      // Reset the version selection when template changes
      formik.setFieldValue('prompt_template_version', []);
    } else {
      setSelectedPromptVersions([]);
      formik.setFieldValue('prompt_template_version', []);
    }
  }, [formik.values.prompt_template_id, promptTemplates]);

  // Filtered lists based on search
  const filteredTemplates = promptTemplates.filter(template =>
    template.name.toLowerCase().includes(templateSearchTerm.toLowerCase()) ||
    template.id.toLowerCase().includes(templateSearchTerm.toLowerCase())
  );

  const filteredVersions = selectedPromptVersions.filter(version => {
    const searchLower = versionSearchTerm.toLowerCase();
    // Remove 'v' prefix if present for version tag matching
    const searchWithoutV = searchLower.startsWith('v') ? searchLower.substring(1) : searchLower;
    
    return (
      version.id.toLowerCase().includes(searchLower) ||
      version.version_tag.toString().includes(searchWithoutV) ||
      `v${version.version_tag}`.toLowerCase().includes(searchLower)
    );
  });

  const fetchInitialData = async () => {
    setLoading(true);
    
    try {
      // Fetch metrics
      try {
        const metricsData = await evaluationService.getMetrics();
        
        // Use the response as-is since it's already in the correct format
        setAvailableMetrics(metricsData || {});
      } catch (metricsError) {
        console.error('❌ Error fetching metrics:', metricsError);
        setAvailableMetrics({});
      }

      // Fetch datasets
      try {
        const datasetsData = await datasetService.getAllDatasets();
        
        // Ensure datasetsData is an array
        const datasetsArray = Array.isArray(datasetsData) ? datasetsData : [];
        const activeDatasets = datasetsArray.filter((d: any) => d.is_active);
        setDatasets(activeDatasets);
      } catch (datasetsError) {
        console.error('❌ Error fetching datasets:', datasetsError);
        setDatasets([]);
      }

      // Fetch deployments
      try {
        const deploymentsData = await api.get(API_ROUTES.DEPLOYMENT_INSTANCES);
        
        const deploymentsArray = Array.isArray(deploymentsData.data) 
          ? deploymentsData.data 
          : deploymentsData.data?.items || [];
        setDeployments(deploymentsArray);
      } catch (deploymentsError) {
        console.error('❌ Error fetching deployments:', deploymentsError);
        setDeployments([]);
      }

      // Fetch prompts
      try {        
        const promptsData = await api.get(API_ROUTES.PROMPTS);

        let templatesArray = [];
        if (Array.isArray(promptsData.data)) {
          templatesArray = promptsData.data;
        } else if (Array.isArray(promptsData)) {
          templatesArray = promptsData;
        }
        
        if (templatesArray.length > 0) {
          
          // Transform templates to our format
          const transformedPrompts = templatesArray.map((template: any) => {
            const templateId = template.id;
            const templateName = template.name || `Template ${templateId}`;
            
            // Extract version objects with both ID and tag
            const versions = (template.versions || [])
              .map((v: any) => ({
                id: v.id || v.version_id || `version_${v.version_tag}`,
                version_tag: v.version_tag || 1,
              }))
              .filter((v: PromptVersion) => !!v.id);
            
            
            return {
              id: templateId,
              name: templateName,
              versions: versions,
            };
          }).filter(t => t.versions.length > 0); // Only include templates with versions
          
          setPromptTemplates(transformedPrompts);
        } else {
          console.warn('⚠️ No prompt templates found in the response');
          setPromptTemplates([]);
        }
      } catch (promptsError: any) {
        console.error('❌ Error fetching prompts:', promptsError);
        console.error('Prompts error message:', promptsError.message);
        console.error('Prompts error response:', promptsError.response);
        setPromptTemplates([]);
      }
      
    } catch (err: any) {
      console.error('❌ ERROR in fetchInitialData:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error stack:', err.stack);
      toast.error('Failed to load form data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleMetricToggle = (metricName: string) => {
    const currentMetrics = formik.values.selectedMetrics;
    const newMetrics = currentMetrics.includes(metricName)
      ? currentMetrics.filter(m => m !== metricName)
      : [...currentMetrics, metricName];
    
    formik.setFieldValue('selectedMetrics', newMetrics);
  };

  const handleVersionChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    formik.setFieldValue('prompt_template_version', typeof value === 'string' ? [] : value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'auto', py: 1.5 }}>
      <Container maxWidth="lg" sx={{ py: 0 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
            Run Evaluation
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Configure and run evaluation with dataset, deployment, and metrics
          </Typography>
        </Box>

        {/* Run Evaluation Form */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', p: 2.5 }}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Name Field */}
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Evaluation Name"
                placeholder="Enter evaluation name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />

              {/* Dataset Dropdown */}
              <FormControl 
                fullWidth 
                error={formik.touched.dataset_id && Boolean(formik.errors.dataset_id)}
              >
                <InputLabel id="dataset-label">Dataset</InputLabel>
                <Select
                  labelId="dataset-label"
                  id="dataset_id"
                  name="dataset_id"
                  value={formik.values.dataset_id}
                  label="Dataset"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {!datasets || datasets.length === 0 ? (
                    <MenuItem value="" disabled>
                      No datasets available
                    </MenuItem>
                  ) : (
                    datasets.map((dataset) => (
                      <MenuItem key={dataset.dataset_id} value={dataset.dataset_id}>
                        {dataset.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formik.touched.dataset_id && formik.errors.dataset_id && (
                  <FormHelperText>{formik.errors.dataset_id}</FormHelperText>
                )}
              </FormControl>

              {/* Deployment Dropdown - Multi-select */}
              <FormControl 
                fullWidth 
                error={formik.touched.deployment_id && Boolean(formik.errors.deployment_id)}
              >
                <InputLabel id="deployment-label">Deployments</InputLabel>
                <Select
                  labelId="deployment-label"
                  id="deployment_id"
                  name="deployment_id"
                  multiple
                  value={formik.values.deployment_id}
                  label="Deployments"
                  onChange={(e) => {
                    const value = e.target.value;
                    const newValue = typeof value === 'string' ? value.split(',') : value;
                    formik.setFieldValue('deployment_id', newValue);
                  }}
                  onBlur={formik.handleBlur}
                  input={<OutlinedInput label="Deployments" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value: string) => {
                        const deployment = deployments.find((d: any) => (d.instance_id || d.id) === value);
                        return (
                          <Chip 
                            key={value} 
                            label={deployment?.instance_name || deployment?.name || value}
                            size="small" 
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {!deployments || deployments.length === 0 ? (
                    <MenuItem value="" disabled>
                      No deployments available
                    </MenuItem>
                  ) : (
                    deployments.map((deployment: any) => {
                      const deploymentId = deployment.instance_id || deployment.id;
                      const isSelected = formik.values.deployment_id.includes(deploymentId);
                      return (
                        <MenuItem key={deploymentId} value={deploymentId}>
                          {isSelected ? (
                            <CheckBoxIcon sx={{ mr: 1, color: 'primary.main' }} />
                          ) : (
                            <CheckBoxOutlineBlankIcon sx={{ mr: 1 }} />
                          )}
                          {deployment.instance_name || deployment.name || deploymentId}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {formik.touched.deployment_id && formik.errors.deployment_id && (
                  <FormHelperText>{formik.errors.deployment_id as string}</FormHelperText>
                )}
              </FormControl>

              {/* Judge Model Dropdown */}
              <FormControl 
                fullWidth 
                error={formik.touched.judge_model && Boolean(formik.errors.judge_model)}
              >
                <InputLabel id="judge-model-label">Judge Model</InputLabel>
                <Select
                  labelId="judge-model-label"
                  id="judge_model"
                  name="judge_model"
                  value={formik.values.judge_model}
                  label="Judge Model"
                  onChange={(e) => {
                    const selectedValue = e.target.value as string;
                    formik.setFieldValue('judge_model', selectedValue);
                  }}
                  onBlur={formik.handleBlur}
                >
                  {!deployments || deployments.length === 0 ? (
                    <MenuItem value="" disabled>
                      No models available
                    </MenuItem>
                  ) : (
                    deployments.map((deployment: any) => {
                      const deploymentId = deployment.instance_id || deployment.id;
                      return (
                        <MenuItem key={deploymentId} value={deploymentId}>
                          {deployment.instance_name || deployment.name || deploymentId}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {formik.touched.judge_model && formik.errors.judge_model && (
                  <FormHelperText>{formik.errors.judge_model}</FormHelperText>
                )}
              </FormControl>

              {/* Prompt Template and Versions - Side by Side */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Prompt Template Dropdown */}
                <FormControl 
                  sx={{ flex: 1 }}
                  error={formik.touched.prompt_template_id && Boolean(formik.errors.prompt_template_id)}
                >
                  <InputLabel id="prompt-template-label">Prompt Template</InputLabel>
                  <Select
                    labelId="prompt-template-label"
                    id="prompt_template_id"
                    name="prompt_template_id"
                    value={formik.values.prompt_template_id}
                    label="Prompt Template"
                    onChange={(e) => {
                      formik.handleChange(e);
                      setTemplateSearchTerm(''); // Reset search on selection
                    }}
                    onBlur={formik.handleBlur}
                    onOpen={() => setTemplateSearchTerm('')}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                      autoFocus: false,
                    }}
                  >
                    <Box 
                      sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TextField
                        size="small"
                        placeholder="Search templates..."
                        fullWidth
                        value={templateSearchTerm}
                        onChange={(e) => {
                          setTemplateSearchTerm(e.target.value);
                        }}
                        autoFocus
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#f8fafc',
                          },
                        }}
                      />
                    </Box>
                    {!promptTemplates || promptTemplates.length === 0 ? (
                      <MenuItem value="" disabled>
                        No prompt templates available
                      </MenuItem>
                    ) : filteredTemplates.length === 0 ? (
                      <MenuItem value="" disabled>
                        No matching templates found
                      </MenuItem>
                    ) : (
                      filteredTemplates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.prompt_template_id && formik.errors.prompt_template_id && (
                    <FormHelperText>{formik.errors.prompt_template_id}</FormHelperText>
                  )}
                </FormControl>

                {/* Prompt Template Versions (Multi-select) */}
                <FormControl 
                  sx={{ flex: 1 }}
                  error={formik.touched.prompt_template_version && Boolean(formik.errors.prompt_template_version)}
                  disabled={!formik.values.prompt_template_id}
                >
                  <InputLabel id="versions-label">Prompt Template Versions</InputLabel>
                  <Select
                    labelId="versions-label"
                    id="prompt_template_version"
                    name="prompt_template_version"
                    multiple
                    value={formik.values.prompt_template_version}
                    onChange={handleVersionChange}
                    onBlur={formik.handleBlur}
                    onOpen={() => setVersionSearchTerm('')}
                    input={<OutlinedInput label="Prompt Template Versions" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((versionId) => {
                          const version = selectedPromptVersions.find(v => v.id === versionId);
                          return (
                            <Chip key={versionId} label={`v${version?.version_tag || versionId}`} size="small" />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                      autoFocus: false,
                    }}
                  >
                    <Box 
                      sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TextField
                        size="small"
                        placeholder="Search versions..."
                        fullWidth
                        value={versionSearchTerm}
                        onChange={(e) => {
                          setVersionSearchTerm(e.target.value);
                        }}
                        autoFocus
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#f8fafc',
                          },
                        }}
                      />
                    </Box>
                    {!selectedPromptVersions || selectedPromptVersions.length === 0 ? (
                      <MenuItem value="" disabled>
                        {formik.values.prompt_template_id 
                          ? 'No versions available for this template' 
                          : 'Select a prompt template first'}
                      </MenuItem>
                    ) : filteredVersions.length === 0 ? (
                      <MenuItem value="" disabled>
                        No matching versions found
                      </MenuItem>
                    ) : (
                      filteredVersions.map((version) => {
                        const isSelected = formik.values.prompt_template_version.includes(version.id);
                        return (
                          <MenuItem key={version.id} value={version.id}>
                            {isSelected ? (
                              <CheckBoxIcon sx={{ mr: 1, color: 'primary.main' }} />
                            ) : (
                              <CheckBoxOutlineBlankIcon sx={{ mr: 1 }} />
                            )}
                            v{version.version_tag}
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                  {formik.touched.prompt_template_version && formik.errors.prompt_template_version && (
                    <FormHelperText>{formik.errors.prompt_template_version as string}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Metrics Selection */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#0f172a' }}>
                  Select Metrics *
                </Typography>
                
                {/* Dynamically render all metric categories */}
                {Object.entries(availableMetrics).map(([category, metrics]) => (
                  metrics && metrics.length > 0 && (
                    <Paper key={category} sx={{ p: 2, mb: 1.5, bgcolor: '#f8fafc' }}>
                      <FormLabel component="legend" sx={{ fontWeight: 500, mb: 1 }}>
                        {category}
                      </FormLabel>
                      <FormGroup>
                        {metrics.map((metric: Metric) => (
                          <FormControlLabel
                            key={metric.metric_id}
                            control={
                              <Checkbox
                                checked={formik.values.selectedMetrics.includes(metric.metric_name)}
                                onChange={() => handleMetricToggle(metric.metric_name)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">{metric.metric_name}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {metric.description}
                                </Typography>
                              </Box>
                            }
                          />
                        ))}
                      </FormGroup>
                    </Paper>
                  )
                ))}
                
                {Object.keys(availableMetrics).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No metrics available
                  </Typography>
                )}
                
                {formik.touched.selectedMetrics && formik.errors.selectedMetrics && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 1, display: 'block' }}>
                    {formik.errors.selectedMetrics}
                  </Typography>
                )}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                <Button 
                  onClick={() => navigate(ROUTE_PATHS.EVALUATION)}
                  disabled={isSubmitting}
                  sx={{ color: '#64748b' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting}
                  sx={{ 
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' }
                  }}
                  startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                >
                  {isSubmitting ? 'Running...' : 'Run Evaluation'}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
